import { Button, IconButton, MenuItem, Tab, Tabs, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { Delete } from "../../icons";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import useStyles from "./MockDeviceHierarchyPanel.style";

const UNIT_TYPES = ["indoor", "outdoor", "service"];

const HIERARCHY_TABS = [
  { key: "units", label: t`Units` },
  { key: "sensors", label: t`Sensors` },
  { key: "groups", label: t`Groups` },
  { key: "systems", label: t`Systems` },
  { key: "compressors", label: t`Compressors` },
  { key: "powerMeters", label: t`Power Meters` },
];

const MockDeviceHierarchyPanel = ({ deviceId }: { deviceId: string }) => {
  const styles: any = useStyles();
  const mockDeviceHierarchy = useStoreState((state) => state.devices.mockDeviceHierarchy);
  const getMockDeviceHierarchy = useStoreActions((actions) => actions.devices.getMockDeviceHierarchy);
  const createMockHierarchyItem = useStoreActions((actions) => actions.devices.createMockHierarchyItem);
  const deleteMockHierarchyItem = useStoreActions((actions) => actions.devices.deleteMockHierarchyItem);

  const [activeTab, setActiveTab] = useState<string>("units");
  const [newName, setNewName] = useState<string>("");
  const [newUnitType, setNewUnitType] = useState<string>(UNIT_TYPES[0]);
  const [newCompressorUnit, setNewCompressorUnit] = useState<string>("");

  useEffect(() => {
    getMockDeviceHierarchy(deviceId);
  }, [deviceId]);

  const hierarchy = mockDeviceHierarchy[deviceId] || {};
  const units = hierarchy.units || [];

  const handleTabChange = (event: any, value: string) => {
    setActiveTab(value);
    setNewName("");
    setNewCompressorUnit("");
  };

  const handleNameChange = (event: any) => {
    setNewName(event.target.value);
  };

  const handleUnitTypeChange = (event: any) => {
    setNewUnitType(event.target.value);
  };

  const handleCompressorUnitChange = (event: any) => {
    setNewCompressorUnit(event.target.value);
  };

  const handleAddItem = () => {
    const data: any = { name: newName };

    if (activeTab === "units") {
      data.type = newUnitType;
    }

    if (activeTab === "compressors") {
      data.unit = newCompressorUnit;
    }

    createMockHierarchyItem({ deviceId, type: activeTab, data });
    setNewName("");
  };

  const handleDeleteItemClick = (subId: string) => () => {
    deleteMockHierarchyItem({ deviceId, type: activeTab, subId });
  };

  const renderUnitName = (unitId: string) => {
    const unit = units.find((candidate: any) => candidate.id === unitId);
    return unit ? unit.name : unitId;
  };

  const renderItems = () => {
    const items = hierarchy[activeTab] || [];

    if (!items.length) {
      return <div className={styles.emptyState}>{t`No items yet`}</div>;
    }

    return (
      <div className={styles.itemsList}>
        {items.map((item: any) => (
          <div className={styles.itemRow} key={item.id}>
            <span>
              {item.name}
              {activeTab === "units" && <span className={styles.itemMeta}>({item.type})</span>}
              {activeTab === "compressors" && <span className={styles.itemMeta}>{renderUnitName(item.unit)}</span>}
            </span>
            <IconButton size="small" onClick={handleDeleteItemClick(item.id)}>
              <Delete />
            </IconButton>
          </div>
        ))}
      </div>
    );
  };

  const renderAddForm = () => {
    const addDisabled = !newName || (activeTab === "compressors" && !newCompressorUnit);

    return (
      <div className={styles.addForm}>
        <TextField
          label={t`Name`}
          variant="standard"
          className={styles.nameField}
          value={newName}
          onChange={handleNameChange}
        />
        {activeTab === "units" && (
          <TextField
            select
            label={t`Type`}
            variant="standard"
            className={styles.typeField}
            value={newUnitType}
            onChange={handleUnitTypeChange}
          >
            {UNIT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        )}
        {activeTab === "compressors" && (
          <TextField
            select
            label={t`Unit`}
            variant="standard"
            className={styles.typeField}
            value={newCompressorUnit}
            onChange={handleCompressorUnitChange}
            disabled={!units.length}
          >
            {units.map((unit: any) => (
              <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
            ))}
          </TextField>
        )}
        <Button className={styles.addButton} onClick={handleAddItem} disabled={addDisabled}>
          {t`Add`}
        </Button>
      </div>
    );
  };

  return (
    <div className={styles.panelContainer}>
      <Tabs value={activeTab} onChange={handleTabChange}>
        {HIERARCHY_TABS.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>
      {renderItems()}
      {renderAddForm()}
    </div>
  );
};

export default MockDeviceHierarchyPanel;
